// <site-header>: the Tienda, Colecciones and El proyecto panels on hover or click (desktop, one at a time) and the mobile menu (a modal), as in the mockup.
// Search is a plain link to /search; with JS it opens the search panel (predictive-search.js fills it).
// The cart link is handled by cart-drawer.js.
class SiteHeader extends HTMLElement {
  connectedCallback() {
    // connectedCallback runs again if the element is moved in the DOM: bind the element's listeners once,
    // and only the document-level one again (disconnectedCallback removed it)
    if (this.ready) {
      document.addEventListener("keydown", this.onKey);
      document.addEventListener("focusin", this.onFocusIn);
      return;
    }
    this.ready = true;
    // each panel is paired with the bar button that controls it
    this.panels = [...this.querySelectorAll("[data-mega]")]
      .map(mega => ({ mega, btn: this.querySelector(`[data-mega-trigger][aria-controls="${mega.id}"]`) }))
      .filter(p => p.btn);
    this.menu = this.querySelector("[data-mmenu]");
    this.openBtn = this.querySelector("[data-open-menu]");
    this.searchPanel = this.querySelector("[data-search-panel]");
    this.searchLink = this.querySelector("[data-open-search]");
    this.megaT = 0;

    for (const panel of this.panels) this.bindPanel(panel);

    if (this.searchPanel && this.searchLink) {
      this.searchLink.addEventListener("click", e => {
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        // on /search itself the page already has the field
        if (document.querySelector(".search-page")) return;
        e.preventDefault();
        if (this.searchPanel.hidden) this.openSearch();
        else this.closeSearch();
      });
      this.searchPanel.querySelector("[data-close-search]")?.addEventListener("click", () => this.closeSearch());
    }

    if (this.menu && this.openBtn) {
      this.openBtn.addEventListener("click", () => this.openMenu());
      this.menu.querySelector("[data-close-menu]")?.addEventListener("click", () => this.closeMenu());
    }

    this.onKey = e => {
      if (e.key === "Tab" && this.menu && !this.menu.hidden) return this.trapTab(e);
      if (e.key !== "Escape") return;
      if (this.menu && !this.menu.hidden) this.closeMenu();
      else if (this.searchPanel && !this.searchPanel.hidden) this.closeSearch();
      else {
        const open = this.openPanel();
        if (!open) return;
        const inside = open.mega.contains(document.activeElement);
        this.megaHide();
        // give focus back to the button without reopening the panel (focus fires synchronously)
        if (inside) { this.quietFocus = true; open.btn.focus(); this.quietFocus = false; }
      }
    };
    // the menu is a modal: focus that lands outside it (a click on the page, a screen reader) comes back in
    this.onFocusIn = e => {
      if (this.menu && !this.menu.hidden && !this.menu.contains(e.target)) this.menuLinks()[0]?.focus();
    };
    document.addEventListener("keydown", this.onKey);
    document.addEventListener("focusin", this.onFocusIn);
  }

  disconnectedCallback() {
    clearTimeout(this.megaT);
    document.removeEventListener("keydown", this.onKey);
    document.removeEventListener("focusin", this.onFocusIn);
  }

  menuLinks() {
    return [...this.menu.querySelectorAll("a[href], button:not([disabled])")].filter(el => el.offsetParent !== null);
  }

  // Tab and Shift+Tab cycle through the open menu
  trapTab(e) {
    const items = this.menuLinks();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || !this.menu.contains(document.activeElement))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (document.activeElement === last || !this.menu.contains(document.activeElement))) {
      e.preventDefault();
      first.focus();
    }
  }

  // hover, focus, click and keyboard for one button and its panel
  bindPanel({ mega, btn }) {
    const show = () => this.megaShow(mega);
    const later = () => { this.megaT = setTimeout(() => this.megaHide(), 140); };
    btn.addEventListener("mouseenter", show);
    btn.addEventListener("mouseleave", later);
    btn.addEventListener("focus", () => { if (!this.quietFocus) show(); });
    // a button opens on click; the Tienda and Colecciones links keep their own navigation (no preventDefault)
    if (btn.tagName === "BUTTON") btn.addEventListener("click", show);
    mega.addEventListener("mouseenter", show);
    mega.addEventListener("mouseleave", later);
    mega.addEventListener("click", e => { if (e.target.closest("a")) this.megaHide(); });
    // keyboard: close once focus leaves both the button and the panel
    const blur = e => {
      if (!e.relatedTarget || !(mega.contains(e.relatedTarget) || e.relatedTarget === btn)) this.megaHide();
    };
    btn.addEventListener("focusout", blur);
    mega.addEventListener("focusout", blur);
    // keyboard: Tab or ArrowDown from the button goes into the open panel; Tab past its last link
    // goes on to the next bar button (or the logo after the last one), Shift+Tab from its first link goes back to the button
    btn.addEventListener("keydown", e => {
      if (e.key === "ArrowDown" || (e.key === "Tab" && !e.shiftKey && !mega.hidden)) {
        this.megaShow(mega);
        const first = this.megaLinks(mega)[0];
        if (!first) return;
        e.preventDefault();
        first.focus();
      }
    });
    mega.addEventListener("keydown", e => {
      if (e.key !== "Tab") return;
      const links = this.megaLinks(mega);
      if (e.shiftKey && e.target === links[0]) {
        e.preventDefault();
        btn.focus();
      } else if (!e.shiftKey && e.target === links[links.length - 1]) {
        const next = btn.nextElementSibling || this.querySelector(".logo");
        if (!next) return;
        e.preventDefault();
        this.megaHide();
        next.focus();
      }
    });
  }

  openPanel() {
    return this.panels.find(p => !p.mega.hidden);
  }

  // opening one panel closes the others
  megaShow(mega) {
    clearTimeout(this.megaT);
    this.hideSearch();
    for (const p of this.panels) {
      const open = p.mega === mega;
      p.mega.hidden = !open;
      p.mega.setAttribute("aria-hidden", open ? "false" : "true");
      p.btn.setAttribute("aria-expanded", open ? "true" : "false");
    }
  }

  // visible links only: the photo link is display:none under 900px
  megaLinks(mega) {
    return [...mega.querySelectorAll("a")].filter(a => a.offsetParent !== null);
  }

  megaHide() {
    clearTimeout(this.megaT);
    for (const p of this.panels) {
      p.mega.hidden = true;
      p.mega.setAttribute("aria-hidden", "true");
      p.btn.setAttribute("aria-expanded", "false");
    }
  }

  // opening search closes the shop panel and the cart drawer
  openSearch() {
    this.megaHide();
    const cart = document.querySelector("cart-drawer");
    if (cart?.isOpen) cart.close();
    this.searchPanel.hidden = false;
    this.searchLink.setAttribute("aria-expanded", "true");
    this.searchPanel.querySelector("#q")?.focus();
  }

  // empties the field and gives focus back to the "Buscar" link
  closeSearch() {
    this.hideSearch();
    this.searchLink.focus();
  }

  hideSearch() {
    if (!this.searchPanel || this.searchPanel.hidden) return;
    this.searchPanel.hidden = true;
    this.searchLink.setAttribute("aria-expanded", "false");
    this.searchPanel.querySelector("predictive-search")?.reset();
  }

  openMenu() {
    clearTimeout(this.megaT);
    this.hideSearch();
    this.menu.hidden = false;
    this.openBtn.setAttribute("aria-expanded", "true");
    this.menu.querySelector("[data-close-menu]")?.focus();
  }

  closeMenu() {
    clearTimeout(this.megaT);
    this.menu.hidden = true;
    this.openBtn.setAttribute("aria-expanded", "false");
    this.openBtn.focus();
  }
}

if (!customElements.get("site-header")) customElements.define("site-header", SiteHeader);
